/**
 * useEffectiveAccess() — React hook that feeds all data sources into
 * the unified access engine (resolveEffectiveAccess).
 *
 * This is the ONLY hook any component should consume for access decisions.
 */
import { useMemo, useEffect, useRef } from "react";
// useRef is retained for the status-change log dedup ref below

import { useAuth } from "./useAuth";
import { useAdminCheck } from "./useAdminCheck";
import { useSubscriptionContext } from "@/contexts/SubscriptionContext";
import { useInstitutionalStudent } from "./useInstitutionalStudent";
import { useExamSession } from "@/contexts/ExamSessionContext";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import {
  resolveEffectiveAccess,
  logEffectiveAccess,
  type EffectiveAccessResult,
  type EffectiveAccessInput,
} from "@/lib/access-engine";

export function useEffectiveAccess(): EffectiveAccessResult & { input: EffectiveAccessInput } {
  const { user, session, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const {
    hasActiveSubscription,
    hasCohortAccess,
    isInitiallyLoaded,
    isBlocked: isBlockedFromContext,
    hasFreeAccess: hasFreeAccessFromContext,
    needsPasswordSetup: needsPasswordSetupFromContext,
    revalidate: revalidateSubscription,
  } = useSubscriptionContext();
  const {
    isInstitutionalStudent,
    isProgramActive,
    isProgramNotStarted,
    cohortEnded,
    isBaselineRequired,
    hasBaseline,
    statusReliability,
    loading: institutionalLoading,
  } = useInstitutionalStudent();
  const { examInProgress } = useExamSession();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Blocked and free access — now properly sourced from SubscriptionContext
  const isBlocked = isBlockedFromContext;
  const hasFreeAccess = hasFreeAccessFromContext;

  const input: EffectiveAccessInput = useMemo(() => ({
    authLoading,
    user: user ? { id: user.id, email_confirmed_at: user.email_confirmed_at } : null,
    session,
    profileLoading: false, // Profile loading doesn't block access decisions
    isBlocked,
    hasFreeAccess,
    needsPasswordSetup: needsPasswordSetupFromContext,
    adminLoading,
    isAdmin,
    subscriptionLoaded: isInitiallyLoaded,
    hasActiveSubscription,
    hasCohortAccess,
    institutionalLoading,
    isInstitutionalStudent,
    isProgramActive,
    isProgramNotStarted,
    cohortEnded,
    isBaselineRequired,
    hasBaseline,
    statusReliability,
    pathname: location.pathname,
    examInProgress,
  }), [
    authLoading, user?.id, user?.email_confirmed_at, session,
    isBlocked, hasFreeAccess, needsPasswordSetupFromContext, adminLoading, isAdmin,
    isInitiallyLoaded, hasActiveSubscription, hasCohortAccess,
    institutionalLoading, isInstitutionalStudent, isProgramActive,
    isProgramNotStarted, cohortEnded, isBaselineRequired, hasBaseline,
    statusReliability, location.pathname, examInProgress,
  ]);

  const result = useMemo(() => resolveEffectiveAccess(input), [input]);

  // Log on every status/role change
  const prevKey = useRef("");
  useEffect(() => {
    const key = `${result.status}|${result.role}|${result.reason}`;
    if (key !== prevKey.current) {
      prevKey.current = key;
      logEffectiveAccess("useEffectiveAccess", result, { userId: user?.id });
    }
  }, [result, user?.id]);

  // ── Offline paywall bypass protection ─────────────────────────────────
  // As soon as the engine settles on "no entitlement", any locally cached PRO
  // study content is purged so it can never be opened offline. Progress and
  // pending sync operations are preserved.
  const purgedRef = useRef(false);
  useEffect(() => {
    if (result.status !== "ready") return;
    if (result.canTakeQuiz || result.role === "admin") {
      purgedRef.current = false;
      return;
    }
    if (purgedRef.current) return;
    purgedRef.current = true;
    import("@/services/local-database")
      .then(({ localDB }) => localDB.purgeProtectedContent())
      .catch((e) => console.warn("[useEffectiveAccess] cache purge failed", e));
  }, [result.status, result.canTakeQuiz, result.role]);


  // Mount-time revalidation removed (Phase 2 dedup).
  // SubscriptionContext.checkSubscription runs on user mount; useInstitutionalStudent uses
  // staleTime=60s + throttled visibility listener. Forcing invalidate here duplicated every
  // access RPC on every route mount. Real-time channels + foreground listeners keep data fresh.
  void queryClient;
  void revalidateSubscription;

  return { ...result, input };
}